import nationalities, {
  countriesWithCode,
  countriesWithCodeAr,
  countriesWithFlag,
  countriesWithNationalities,
  nationalitiesWithISO,
} from "@aurem/nationalities";
import Fuse from "fuse.js";
import { useI18n } from "providers/I18nProvider";

const fuseOptions = {
  isCaseSensitive: false,
  includeScore: false,
  shouldSort: true,
  includeMatches: false,
  findAllMatches: false,
  minMatchCharLength: 3,
  location: 0,
  threshold: 0.6,
  distance: 100,
  useExtendedSearch: false,
  ignoreLocation: false,
  ignoreFieldNorm: false,
  fieldNormWeight: 1,
};
const countries = Object.entries(countriesWithNationalities).map(
  (item) => item[0],
);

export function parseAndValidateNationality(
  value: string,
  t: ReturnType<typeof useI18n>["t"],
): {
  error: boolean;
  errorMessage: string;
} {
  value = value.trim();
  let fuzzyMatch;

  if (!nationalities.includes(value)) {
    const fuseNationalities = new Fuse(nationalities, fuseOptions);
    const nationalityFuzzyMatch = fuseNationalities.search(value);
    if (nationalityFuzzyMatch[0]) {
      fuzzyMatch = nationalityFuzzyMatch[0].item;
    }

    const fuseCountries = new Fuse(countries, fuseOptions);
    const countryFuzzyMatch = fuseCountries.search(value);
    if (countryFuzzyMatch[0]) {
      fuzzyMatch = countriesWithNationalities[countryFuzzyMatch[0].item];
    }

    return {
      error: true,
      errorMessage: fuzzyMatch
        ? t("apps.admin.validation.nationality.invalidWithHint", fuzzyMatch)
        : t("apps.admin.validation.nationality.invalid"),
    };
  }

  return { error: false, errorMessage: "" };
}

export const getCountriesWithCode = (lang: string) => {
  const countriesAndCode =
    lang === "ar" ? countriesWithCodeAr : countriesWithCode;
  return countriesAndCode;
};

export const getCountryName = (countryCode: string, lang: string) => {
  const countries = lang === "ar" ? countriesWithCodeAr : countriesWithCode;
  return countries[countryCode as keyof typeof countries] || "-";
};

export const getCountryFlag = (countryCode: string) => {
  const countryFlag = countriesWithFlag.find(
    (item) => item.code === countryCode,
  );
  return countryFlag ? countryFlag.flag : "";
};

export const citizenshipByCountryCode = Object.entries(
  nationalitiesWithISO,
).reduce<Record<string, string>>((acc, [citizenship, iso]) => {
  const [code] = iso.split("-");
  acc[code] ??= citizenship;
  return acc;
}, {});
